import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Home } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function DashboardShell({ title, subtitle, nav, active, onNav, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-secondary/60 flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-72 bg-foreground text-background p-6 fixed inset-y-0 z-40">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <span className="inline-flex items-center justify-center rounded-2xl bg-background px-3 py-2">
            <img src="/logo.png" alt="NearbyDeals" className="h-9 w-auto" />
          </span>
        </Link>

        <p className="text-xs uppercase tracking-widest text-background/40 font-bold mb-4">Menu</p>
        <nav className="flex-1 space-y-1">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <button
                key={n.key}
                data-testid={`dash-nav-${n.key}`}
                onClick={() => onNav(n.key)}
                className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold transition-colors ${
                  active === n.key
                    ? "bg-primary text-primary-foreground"
                    : "text-background/70 hover:bg-background/10"
                }`}
              >
                <Icon className="w-4 h-4" /> {n.label}
              </button>
            );
          })}
        </nav>

        <div className="pt-4 border-t border-background/15 space-y-1">
          <button
            onClick={() => navigate("/")}
            className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-background/70 hover:bg-background/10 transition-colors"
          >
            <Home className="w-4 h-4" /> Back to site
          </button>
          <button
            data-testid="dash-logout"
            onClick={() => {
              logout();
              navigate("/");
            }}
            className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold text-background/70 hover:bg-background/10 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-72">
        <header className="bg-background/80 backdrop-blur-xl border-b border-border px-5 sm:px-8 py-6 sticky top-0 z-30">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">{title}</h1>
              {subtitle && <p className="text-muted-foreground text-sm mt-1">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:grid place-items-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold uppercase">
                {user?.name?.[0]}
              </span>
              <div className="hidden sm:block">
                <p className="font-semibold text-sm leading-tight">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace("_", " ")}</p>
              </div>
            </div>
          </div>

          {/* mobile nav */}
          <div className="lg:hidden flex gap-2 overflow-x-auto mt-5 -mx-1 px-1">
            {nav.map((n) => (
              <button
                key={n.key}
                onClick={() => onNav(n.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  active === n.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
                }`}
              >
                {n.label}
              </button>
            ))}
          </div>
        </header>

        <main className="p-5 sm:p-8">{children}</main>
      </div>
    </div>
  );
}
